import * as React from 'react';
import { TableRow, TableCell, Collapse, Box, Typography, Select, MenuItem, Checkbox, FormControlLabel, Stack } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import PanoramaFishEyeIcon from '@mui/icons-material/PanoramaFishEye';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';

import { Question } from '../../../../Models/Assessment/AssessmentCategory.model';
import { SelectionType } from '../../../../Enum/SelectionTypeEnum';
import { AssessmentQuestions } from '../../../../utils/assessmentQuestions';
import DebouncedTextField from './DebouncedTextField';
import { isAIIcon } from '../../Assessment';

interface SubQuestionsRowProps {
    question: Question;
    qIndex: number;
    colSpan: number;
    scoreTotal?: number;
    onUpdateSubQuestionAnswer: (isSelected: boolean, qIndex: number, oIndex: number, subQIndex: number, sqIndex: number) => void;
    onUpdateSubQuestionAnswerText: (value: string | Date | null, qIndex: number, oIndex: number, subQIndex: number) => void;
    onUpdateSubQuestionSubOptionAnswer: (isSelected: boolean, qIndex: number, oIndex: number, subQIndex: number, sqIndex: number, subOptIndex: number) => void;
    onUpdateSubQuestionOptionSubOptionName: (value: string, qIndex: number, oIndex: number, subQIndex: number, sqIndex: number, subOptIndex: number) => void;
}

const boldQuestions = [
    AssessmentQuestions.PhysicalExam_StrengthSummary.toString(),
    AssessmentQuestions.PhysicalExam_ReasonForExam.toString(),
    AssessmentQuestions.PhysicalExam_ClinicalImpressions.toString(),
    AssessmentQuestions.PhysicalExam_OverallFallRisk.toString(),
];

const renderScoringKey = (scoreTotal?: number) => {
    return (
        <Stack spacing={1} sx={{ my: 2, ml: -4 }}>
            {scoreTotal !== undefined && (
                <Typography variant="h6" sx={{ fontWeight: 600, pb: 2, color: 'primary.main' }}>
                    TOTAL SCORE: {scoreTotal}/30
                </Typography>
            )}
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Interpretation (Guideline Only)
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, pl: 4 }}>
                <b>24-30:</b> No cognitive impairment suggested
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, pl: 4 }}>
                <b>18-23:</b> Mild cognitive impairment suggested
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, pl: 4 }}>
                <b>0-17:</b> Moderate-severe cognitive impairment suggested
            </Typography>
        </Stack>
    );
};

const renderGroupLabel = (question: Question) => {
    let label: string | null = null;
    switch (question.Description) {
        case AssessmentQuestions.PhysicalExam_Posture:
            label = 'General Observation';
            break;
        case AssessmentQuestions.PhysicalExam_Tone:
            label = 'Muscle Tone & Range of Motion';
            break;
        case AssessmentQuestions.PhysicalExam_RapidAlternatingMovements:
            label = 'Coordination';
            break;
        case AssessmentQuestions.PhysicalExam_GaitGMM:
            label = 'Gait & Balance';
            break;
        case AssessmentQuestions.PhysicalExam_SitToStand:
            label = 'Functional Mobility';
            break;
        case AssessmentQuestions.PhysicalExam_HistoryOfFalls:
            label = 'Fall Risk Indicators';
            break;
        case AssessmentQuestions.PhysicalExam_Time:
            label = '1. Orientation (10 pts)';
            break;
        case AssessmentQuestions.PhysicalExam_AskMemberToRepeat:
            label = '2. Registration (3 pts)';
            break;
        case AssessmentQuestions.PhysicalExam_ChooseOne:
            label = '3. Attention & Calculation (5 pts)';
            break;
        case AssessmentQuestions.PhysicalExam_Recall3Words:
            label = '4. Recall (3 pts)';
            break;
        case AssessmentQuestions.PhysicalExam_Naming:
            label = '5. Language & Visuospatial (9 pts)';
            break;
        default:
            label = null;
            break;
    }

    return label ? (
        <Typography variant="body2" sx={{ fontWeight: 600, textDecoration: 'underline', ml: -4, my: 2 }}>
            {label}
        </Typography>
    ) : null;
};

const SubQuestionsRow: React.FC<SubQuestionsRowProps> = React.memo(
    ({
        question,
        qIndex,
        colSpan,
        scoreTotal,
        onUpdateSubQuestionAnswer,
        onUpdateSubQuestionAnswerText,
        onUpdateSubQuestionSubOptionAnswer,
        onUpdateSubQuestionOptionSubOptionName,
    }) => {
        // Find the first selected option that has SubQuestions
        const selectedOptionWithSubQuestions = question?.Options?.find((opt) => opt.IsSelected && opt.SubQuestions?.length > 0);
        if (!selectedOptionWithSubQuestions) return null;

        const oIndex = question.Options.indexOf(selectedOptionWithSubQuestions);

        return (
            <TableRow key={`${question.Id}_subquestions_${qIndex}`}>
                <TableCell colSpan={colSpan} sx={{ py: 1, backgroundColor: '#f5f5f5' }}>
                    <Collapse in={true} timeout="auto">
                        <Box sx={{ pl: 5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {selectedOptionWithSubQuestions.SubQuestions.map((subQuestion, subQIndex) => {
                                const subQSelectionType = subQuestion.SelectionType || SelectionType.SingleSelect;
                                const isBold = boldQuestions.includes(subQuestion.Description);
                                const hasNoneOption = subQuestion.Options?.some((option) => option.Name === 'None') ?? false;
                                const isNoneSelected = subQuestion.Options?.some((option) => option.Name === 'None' && option.IsSelected) ?? false;

                                return (
                                    <Box
                                        key={`subquestion_${subQuestion.Id || subQIndex}_${question.Id}`}
                                        sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                                    >
                                        {/* Display label for non-text SubQuestions */}
                                        {subQSelectionType !== SelectionType.Text && subQSelectionType !== SelectionType.DateTime && (
                                            <Stack>
                                                {subQuestion.Description !== AssessmentQuestions.PhysicalExam_ClinicalImpressions
                                                    ? renderGroupLabel(subQuestion)
                                                    : renderScoringKey(scoreTotal)}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ...(!isBold ? { mb: 1 } : { mt: 2, ml: -4 }) }}>
                                                    <Typography
                                                        variant="body2"
                                                        sx={isBold ? { fontWeight: 600, fontSize: '1rem' } : { fontWeight: 500 }}
                                                    >
                                                        {subQuestion.Description}
                                                    </Typography>
                                                    {subQuestion.IsAIAnswer && isAIIcon(subQuestion)}
                                                </Box>
                                            </Stack>
                                        )}

                                        {/* For Text type SubQuestions, show TextField */}
                                        {subQSelectionType === SelectionType.Text && (
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                                {subQuestion.IsAIAnswer && isAIIcon(subQuestion)}
                                                <DebouncedTextField
                                                    size="small"
                                                    label={subQuestion.Description || 'Enter text...'}
                                                    placeholder={subQuestion.Description || ''}
                                                    value={subQuestion.AnswerText || ''}
                                                    sx={{ minWidth: 200, flex: 1 }}
                                                    onChange={(val) => onUpdateSubQuestionAnswerText(val, qIndex, oIndex, subQIndex)}
                                                />
                                            </Box>
                                        )}

                                        {/* For DateTime type SubQuestions, show DatePicker */}
                                        {subQSelectionType === SelectionType.DateTime && (
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                                {subQuestion.IsAIAnswer && isAIIcon(subQuestion)}
                                                <DesktopDatePicker
                                                    label={subQuestion.Description || 'Select Date'}
                                                    format="MM/dd/yyyy"
                                                    value={subQuestion.AnswerText ? new Date(subQuestion.AnswerText) : null}
                                                    onChange={(newValue) => onUpdateSubQuestionAnswerText(newValue, qIndex, oIndex, subQIndex)}
                                                    slotProps={{ textField: { size: 'small', sx: { minWidth: 200 } } }}
                                                />
                                            </Box>
                                        )}

                                        {/* For SingleSelect/MultiSelect, render Options */}
                                        {(subQSelectionType === SelectionType.SingleSelect || subQSelectionType === SelectionType.MultiSelect) && (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 2,
                                                        flexWrap: 'wrap',
                                                        alignItems: 'center',
                                                        pl: 4,
                                                    }}
                                                >
                                                    {subQuestion.Options?.map((subQOption, sqIndex) => (
                                                        <FormControlLabel
                                                            key={`sub_question_option_${subQOption.Id || sqIndex}_${question.Id}`}
                                                            sx={{ margin: 0 }}
                                                            control={
                                                                <Checkbox
                                                                    checked={subQOption.IsSelected || false}
                                                                    size="small"
                                                                    disabled={hasNoneOption && isNoneSelected && subQOption.Name !== 'None'}
                                                                    onChange={(e) =>
                                                                        onUpdateSubQuestionAnswer(e.target.checked, qIndex, oIndex, subQIndex, sqIndex)
                                                                    }
                                                                    {...(subQSelectionType === SelectionType.SingleSelect
                                                                        ? { icon: <PanoramaFishEyeIcon />, checkedIcon: <RadioButtonCheckedIcon /> }
                                                                        : {})}
                                                                />
                                                            }
                                                            label={subQOption.Name}
                                                        />
                                                    ))}
                                                </Box>

                                                {/* Render SubOptions for SubQuestion Option */}
                                                {subQuestion.Options?.map((subQOption, sqIndex) => {
                                                    if (!subQOption.IsSelected || !subQOption.SubOptions || subQOption.SubOptions.length === 0) return null;

                                                    const hasNoneSubOptions = subQOption.SubOptions.some((subOpt) => subOpt.Name === 'None');
                                                    const isNoneSubOptionSelected = subQOption.SubOptions.some(
                                                        (subOpt) => subOpt.Name === 'None' && subOpt.IsSelected
                                                    );

                                                    return (
                                                        <Box
                                                            key={`sub_question_option_container_${subQOption.Id || sqIndex}`}
                                                            sx={{
                                                                pl: 8,
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: 0.5,
                                                                mt: 0.5,
                                                            }}
                                                        >
                                                            {subQOption.SubOptions.map((subOpt, subOptIndex) => {
                                                                const subOptSelectionType = subOpt.SelectionType || SelectionType.SingleSelect;

                                                                return (
                                                                    <Box key={`sub_q_sub_opt_${subOpt.Id || subOptIndex}`}>
                                                                        {(subOptSelectionType === SelectionType.SingleSelect ||
                                                                            subOptSelectionType === SelectionType.MultiSelect) && (
                                                                            <FormControlLabel
                                                                                control={
                                                                                    <Checkbox
                                                                                        checked={subOpt.IsSelected || false}
                                                                                        size="small"
                                                                                        disabled={
                                                                                            hasNoneSubOptions &&
                                                                                            isNoneSubOptionSelected &&
                                                                                            subOpt.Name !== 'None'
                                                                                        }
                                                                                        onChange={(e) =>
                                                                                            onUpdateSubQuestionSubOptionAnswer(
                                                                                                e.target.checked,
                                                                                                qIndex,
                                                                                                oIndex,
                                                                                                subQIndex,
                                                                                                sqIndex,
                                                                                                subOptIndex
                                                                                            )
                                                                                        }
                                                                                        {...(subOptSelectionType === SelectionType.SingleSelect
                                                                                            ? {
                                                                                                  icon: <PanoramaFishEyeIcon />,
                                                                                                  checkedIcon: <RadioButtonCheckedIcon />,
                                                                                              }
                                                                                            : {})}
                                                                                    />
                                                                                }
                                                                                label={subOpt.Name}
                                                                            />
                                                                        )}

                                                                        {subOptSelectionType === SelectionType.Text && (
                                                                            <Box
                                                                                sx={{
                                                                                    display: 'flex',
                                                                                    flexDirection: 'column',
                                                                                    gap: 0.5,
                                                                                }}
                                                                            >
                                                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                                    {subOpt.Name}
                                                                                </Typography>
                                                                                <DebouncedTextField
                                                                                    size="small"
                                                                                    fullWidth
                                                                                    placeholder="Enter text..."
                                                                                    value={subOpt.TextAnswer || ''}
                                                                                    onChange={(val) =>
                                                                                        onUpdateSubQuestionOptionSubOptionName(
                                                                                            val,
                                                                                            qIndex,
                                                                                            oIndex,
                                                                                            subQIndex,
                                                                                            sqIndex,
                                                                                            subOptIndex
                                                                                        )
                                                                                    }
                                                                                />
                                                                            </Box>
                                                                        )}
                                                                    </Box>
                                                                );
                                                            })}
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        );
    }
);

SubQuestionsRow.displayName = 'SubQuestionsRow';

export default SubQuestionsRow;
